import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CurrencyCtx = createContext(null);

const COUNTRY_CURRENCY = {
  ZA: "ZAR", US: "USD", GB: "GBP", NG: "NGN", KE: "KES", GH: "GHS", BW: "BWP", NA: "NAD", ZM: "ZMW",
  UG: "UGX", TZ: "TZS", IN: "INR", BR: "BRL", AU: "AUD", CA: "CAD", JP: "JPY", AE: "AED", MX: "MXN",
  DE: "EUR", FR: "EUR", ES: "EUR", IT: "EUR", NL: "EUR", PT: "EUR", IE: "EUR", BE: "EUR", AT: "EUR",
  FI: "EUR", GR: "EUR",
};

export const DIAL_CODES = {
  ZA: "+27", US: "+1", GB: "+44", NG: "+234", KE: "+254", GH: "+233", BW: "+267", NA: "+264", ZM: "+260",
  UG: "+256", TZ: "+255", IN: "+91", BR: "+55", AU: "+61", CA: "+1", JP: "+81", AE: "+971", MX: "+52",
  DE: "+49", FR: "+33", ES: "+34", IT: "+39", NL: "+31", PT: "+351", IE: "+353",
};

export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const [rates, setRates] = useState({ USD: 1 });
  const [currencies, setCurrencies] = useState(["USD"]);
  const [limits, setLimits] = useState({ min_deposit_zar: 50, min_withdraw_zar: 100 });
  const [country, setCountry] = useState(localStorage.getItem("pp_country") || "ZA");
  const [currency, setCurrencyState] = useState(localStorage.getItem("pp_currency") || "");

  useEffect(() => {
    api.get("/fx").then(({ data }) => {
      setRates(data.rates);
      setLimits(data.limits);
      setCurrencies(data.currencies.filter((c) => data.rates[c]));
    });
    axios
      .get("https://ipapi.co/json/")
      .then(({ data }) => {
        if (data?.country_code) {
          setCountry(data.country_code);
          localStorage.setItem("pp_country", data.country_code);
          if (!localStorage.getItem("pp_currency")) {
            setCurrencyState(COUNTRY_CURRENCY[data.country_code] || data.currency || "USD");
          }
        }
      })
      .catch(() => {
        if (!localStorage.getItem("pp_currency")) setCurrencyState("ZAR");
      });
  }, []);

  useEffect(() => {
    if (user && user.currency && !localStorage.getItem("pp_currency")) setCurrencyState(user.currency);
  }, [user]);

  const setCurrency = (c) => {
    setCurrencyState(c);
    localStorage.setItem("pp_currency", c);
    if (user) api.put("/auth/preferences", { currency: c }).catch(() => {});
  };

  const cur = currency && rates[currency] ? currency : rates.ZAR && !currency ? "ZAR" : currency || "USD";
  const rate = rates[cur] || 1;
  const fmt = (usd) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: rates[cur] ? cur : "USD", maximumFractionDigits: 2 }).format(
      (usd || 0) * (rates[cur] ? rate : 1)
    );
  const fromUsd = (usd) => (usd || 0) * rate;
  const fromZar = (zar) => (rates.ZAR && rates[cur] ? (zar / rates.ZAR) * rate : zar);

  return (
    <CurrencyCtx.Provider value={{ currency: rates[cur] ? cur : "USD", setCurrency, currencies, rate, fmt, fromUsd, fromZar, limits, country }}>
      {children}
    </CurrencyCtx.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyCtx);
